/**
 * Euthanasia consent form — interactive form for populating fields inline.
 */
import { Label } from '@/frontend/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignaturePreview } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetailsPreview } from '@/frontend/features/printing/documents/blocks/patient-details'
import { VetSignoffPreview } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import {
  DISPOSITION_OPTIONS,
  defaultEuthanasiaConsentFields,
  type EuthanasiaConsentFields,
  EuthanasiaConsentPdf,
} from '@/frontend/features/printing/documents/euthanasia-consent.print'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

const TITLE = 'EUTHANASIA CONSENT FORM'

function consentText(patientName: string) {
  return `I hereby acknowledge that I am the owner of ${patientName}, and request the veterinary surgeon of the clinic to euthanise this pet.`
}

function paymentNotice(patientName: string) {
  return `Please note that payment for cremation must be made before ${patientName} can be sent to the crematorium.`
}

const FALLBACK_NOTICE =
  'If you are unable to decide on cremation arrangements at this time, please let us know within 10 days of euthanasia. If we do not hear from you within this time, we will proceed with communal cremation, and ashes will not be returned.'

function EuthanasiaConsentForm({
  context,
  value,
  onChange,
}: DocumentFormProps<EuthanasiaConsentFields>) {
  const { patient, owner, clinic } = context
  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 text-center text-lg font-bold underline">{TITLE}</h1>

      <OwnerDetailsPreview owner={owner} />
      <div className="mt-3">
        <PatientDetailsPreview
          patient={patient}
          weight={context.weight}
          showDob
          showWeight
          showMicrochip
        />
      </div>

      {/* Consent paragraph */}
      <p className="mt-4">{consentText(patient.name)}</p>

      {/* Body disposition */}
      <div className="mt-4">
        <p className="mb-2">I request for the body to be:</p>
        <RadioGroup
          value={value.bodyDisposition}
          onValueChange={(v) => onChange({ ...value, bodyDisposition: v })}
        >
          {DISPOSITION_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-start gap-2">
              <RadioGroupItem value={option.value} id={`disposition-${option.value}`} />
              <Label htmlFor={`disposition-${option.value}`} className="font-normal leading-snug">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Notices */}
      <p className="mt-4 font-semibold">{paymentNotice(patient.name)}</p>
      <p className="mt-2">{FALLBACK_NOTICE}</p>

      <OwnerSignaturePreview owner={owner} />
      <VetSignoffPreview
        vetName={value.vetName}
        onChange={(v) => onChange({ ...value, vetName: v.vetName })}
      />
    </DocumentPreview>
  )
}

export const euthanasiaConsent = withDocumentEditor({
  id: 'euthanasia-consent',
  label: 'Euthanasia Consent Form',
  defaultFields: defaultEuthanasiaConsentFields,
  Preview: EuthanasiaConsentForm,
  Pdf: EuthanasiaConsentPdf,
})
